import { useEffect, useState } from 'react';
import { Shield, Settings as SettingsIcon } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { useUser } from '../../store/useAuthStore';
import { AdminPermissionsModal } from './AdminPermissionsModal';

interface AdminRow {
  _id: string;
  email: string;
  status?: string;
  profile?: { firstName?: string; lastName?: string };
  adminData?: {
    canIssueRefunds?: boolean;
    canRecordPayment?: boolean;
    canMoveHierarchy?: boolean;
    canCompOrders?: boolean;
  };
  createdAt?: string;
}

export function AdminsListView() {
  const currentUser = useUser();
  const [admins, setAdmins] = useState<AdminRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<AdminRow | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get<{ success: boolean; data: AdminRow[] }>('/users/admins');
      if (res.success) {
        setAdmins(res.data);
      } else {
        setError('Failed to load admins');
      }
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load admins');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Admins</h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage admin accounts and per-admin permission flags.
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">{error}</div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Admin</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Refunds</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Move hierarchy</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Record payment</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Comp orders</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500">Loading…</td></tr>
            ) : admins.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500">No admin accounts.</td></tr>
            ) : (
              admins.map(admin => {
                const isSelf = currentUser?._id === admin._id;
                const displayName = [admin.profile?.firstName, admin.profile?.lastName]
                  .filter(Boolean)
                  .join(' ') || '—';
                return (
                  <tr key={admin._id} className={isSelf ? 'bg-blue-50/40' : ''}>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-blue-600" />
                        <span>{displayName}</span>
                        {isSelf && <span className="text-xs text-blue-700">(you)</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{admin.email}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{admin.status || '—'}</td>
                    <td className="px-4 py-3 text-sm">
                      {admin.adminData?.canIssueRefunds ? (
                        <span className="px-2 py-0.5 text-xs rounded bg-orange-100 text-orange-800">Enabled</span>
                      ) : (
                        <span className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-600">Off</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {admin.adminData?.canMoveHierarchy ? (
                        <span className="px-2 py-0.5 text-xs rounded bg-purple-100 text-purple-800">Enabled</span>
                      ) : (
                        <span className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-600">Off</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {admin.adminData?.canRecordPayment ? (
                        <span className="px-2 py-0.5 text-xs rounded bg-green-100 text-green-800">Enabled</span>
                      ) : (
                        <span className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-600">Off</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {admin.adminData?.canCompOrders ? (
                        <span className="px-2 py-0.5 text-xs rounded bg-pink-100 text-pink-800">Enabled</span>
                      ) : (
                        <span className="px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-600">Off</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setEditing(admin)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-700 bg-white border border-blue-200 rounded-md hover:bg-blue-50"
                      >
                        <SettingsIcon className="w-3 h-3" />
                        Permissions
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <AdminPermissionsModal
          admin={editing}
          isSelf={currentUser?._id === editing._id}
          onClose={() => setEditing(null)}
          onSuccess={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}
